// src/lib/queryClient.ts
import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Cache data for 5 minutes
      staleTime: 5 * 60 * 1000,
      // Keep unused data in cache for 30 minutes
      gcTime: 30 * 60 * 1000,
      // Retry failed requests 3 times
      retry: 3,
      // Exponential backoff for retries
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      // Refetch on window focus (useful for stale data)
      refetchOnWindowFocus: true,
      // Don't refetch on reconnect by default
      refetchOnReconnect: true,
      // Don't refetch on mount if data is fresh
      refetchOnMount: true,
    },
    mutations: {
      // Retry mutations once on failure
      retry: 1,
      retryDelay: 1000,
    },
  },
});

// Query keys factory for consistent cache keys
export const queryKeys = {
  // Posts
  posts: {
    all: ['posts'] as const,
    lists: () => [...queryKeys.posts.all, 'list'] as const,
    list: (filters: Record<string, unknown>) => [...queryKeys.posts.lists(), filters] as const,
    infinite: (filters?: Record<string, unknown>) => [...queryKeys.posts.all, 'infinite', filters] as const,
    details: () => [...queryKeys.posts.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.posts.details(), id] as const,
    comments: (postId: number | string) => [...queryKeys.posts.detail(postId), 'comments'] as const,
  },

  // Users
  users: {
    all: ['users'] as const,
    lists: () => [...queryKeys.users.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.users.lists(), filters] as const,
    details: () => [...queryKeys.users.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.users.details(), id] as const,
    current: () => [...queryKeys.users.all, 'current'] as const,
  },

  // Verifications
  verifications: {
    all: ['verifications'] as const,
    lists: () => [...queryKeys.verifications.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.verifications.lists(), filters] as const,
    details: () => [...queryKeys.verifications.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.verifications.details(), id] as const,
    pending: () => [...queryKeys.verifications.all, 'pending'] as const,
  },

  // Reports
  reports: {
    all: ['reports'] as const,
    lists: () => [...queryKeys.reports.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.reports.lists(), filters] as const,
    details: () => [...queryKeys.reports.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.reports.details(), id] as const,
    pending: () => [...queryKeys.reports.all, 'pending'] as const,
  },

  // Blocked Users
  blockedUsers: {
    all: ['blockedUsers'] as const,
    lists: () => [...queryKeys.blockedUsers.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.blockedUsers.lists(), filters] as const,
  },

  // Events
  events: {
    all: ['events'] as const,
    lists: () => [...queryKeys.events.all, 'list'] as const,
    list: (filters?: Record<string, unknown>) => [...queryKeys.events.lists(), filters] as const,
    details: () => [...queryKeys.events.all, 'detail'] as const,
    detail: (id: number | string) => [...queryKeys.events.details(), id] as const,
  },

  // Analytics
  analytics: {
    all: ['analytics'] as const,
    overview: () => [...queryKeys.analytics.all, 'overview'] as const,
    users: (period?: number) => [...queryKeys.analytics.all, 'users', period] as const,
    engagement: (period?: number) => [...queryKeys.analytics.all, 'engagement', period] as const,
    platform: () => [...queryKeys.analytics.all, 'platform'] as const,
    alarms: (period?: number) => [...queryKeys.analytics.all, 'alarms', period] as const,
  },

  // Notifications
  notifications: {
    all: ['notifications'] as const,
    unread: () => [...queryKeys.notifications.all, 'unread'] as const,
  },

  // Files
  files: {
    all: ['files'] as const,
    folder: (folderId?: number | string | null) => [...queryKeys.files.all, 'folder', folderId] as const,
    recent: () => [...queryKeys.files.all, 'recent'] as const,
    favorites: () => [...queryKeys.files.all, 'favorites'] as const,
    trash: () => [...queryKeys.files.all, 'trash'] as const,
  },
};

export default queryClient;
